import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { api, API, apiError } from "@/lib/api";

function Row({ label, value, testid, mono = false, tone = "text-white" }) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.05] py-3.5 last:border-0">
      <span className="text-sm text-neutral-400">{label}</span>
      <span className={`text-sm font-bold ${mono ? "font-mono" : ""} ${tone}`} data-testid={testid}>{value}</span>
    </div>
  );
}

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const webhookUrl = `${API}/payments/razorpay/webhook`;

  useEffect(() => {
    api.get("/admin/payments/status")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load payment settings")));
  }, []);

  if (!data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  }

  const rz = data.razorpay;

  return (
    <div data-testid="admin-payments">
      <section className="max-w-2xl rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-6" data-testid="payments-status">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="h-5 w-5 text-amber-500" />
          <h3 className="font-display text-lg font-medium tracking-tight">Payment Gateway — Razorpay</h3>
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          Keys live in server environment variables. The Key Secret and Webhook Secret are never shown here or sent to the browser.
        </p>

        <div className="mt-5">
          <Row label="Razorpay" testid="rzp-enabled"
            value={rz.enabled ? "Enabled" : "Disabled"}
            tone={rz.enabled ? "text-emerald-400" : "text-red-400"} />
          <Row label="Mode" testid="rzp-mode"
            value={rz.enabled ? (rz.mode === "test" ? "TEST — no real money" : "LIVE") : "—"}
            tone={rz.mode === "test" ? "text-amber-400" : "text-emerald-400"} />
          <Row label="Key ID" testid="rzp-key-id" value={rz.key_id || "—"} mono />
          <Row label="Key Secret" testid="rzp-secret" value={rz.enabled ? "•••••••• (server-only)" : "—"} />
          <Row label="Webhook" testid="rzp-webhook"
            value={rz.webhook_configured ? "Secret configured" : "Not configured"}
            tone={rz.webhook_configured ? "text-emerald-400" : "text-amber-400"} />
        </div>

        {rz.enabled && (
          <div className="mt-5 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid="webhook-setup">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Webhook endpoint (enter in Razorpay Dashboard → Settings → Webhooks)</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-amber-300" data-testid="webhook-url">{webhookUrl}</code>
              <button
                data-testid="webhook-copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(webhookUrl);
                    toast.success("Webhook URL copied");
                  } catch {
                    const ta = document.createElement("textarea");
                    ta.value = webhookUrl;
                    document.body.appendChild(ta);
                    ta.select();
                    const ok = document.execCommand("copy");
                    ta.remove();
                    ok ? toast.success("Webhook URL copied") : toast.error("Copy failed — select the URL manually");
                  }
                }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-amber-500/50 hover:text-amber-400"
                aria-label="Copy webhook URL"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-neutral-500">
              {rz.webhook_configured
                ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Enable events: payment.captured, payment.failed — use the webhook secret provided to you separately.</>
                : <><XCircle className="h-3.5 w-3.5 text-amber-400" /> Webhook secret missing — add RAZORPAY_WEBHOOK_SECRET to the server environment.</>}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
